export const meta = {
  id: '48-comparison-and-word-building',
  order: 48,
  checkFocus: { degree: ['comparative', 'superlative', 'equative'] },   // what the end-of-lesson check tests first
  titleFi: 'Vertailu ja sananmuodostus',
  titleEn: 'Comparison and word building',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: [
    'trūma',
    'zūger',
    'gīda',
    'sȳndor',
    'myrdys',
    'lēda-2',
    'gīs',
    'eglie',
    'pāsābare-2',
    'oznehure-2',
    'kȳvanon',
    'gūrēntir',
    'hēdrȳ',
    'brāedāzma',
    'vīlībāzma',
    'geralbar',
    'egralbar',
    'riñītsos',
    'perzītsos',
    'pirtiapos',
    'brōziapos',
    'bartanna',
    'kaerīnio',
    'qrimpālekio',
    'laehurlion',
    'ērinnon',
    'gevives',
    'nākostōba',
    'quptenka',
    'valyrīha',
    'mōrqitta',
    'laehurloqitta',
    'rīza',
    'valzȳrys',
    'ēlie',
    'tȳne',
    'ours:nāhēnkāves',
    'ours:mēnenka',
    'ours:gīhenka',
    'ours:qanāves',
    'ours:hēnkemagon',
    'ours:gārion',
    'ours:generationi',
  ],
  exampleIds: [
    'ex-23f825fba84a',
    'ex-3c3d21288ab8',
    'ex-991b56eca6b7',
    'ex-7f34a1239c38',
    'ex-453c664c3c21',
    'ex-c95a57401155',
    'ex-051788fbace1',
    'ex-c63fa45474e2',
    'ex-34b291c49825',
    'ex-c8933dfdc449',
    'ex-a8cb7727a1a8',
    'hotd-s1e01-020',
    'hotd-s2e04-006',
    'hotd-s3e01-009',
    'got-s7e04-001',
    'got-s6e01-002',
    'hotd-s2e03-001',
    'hotd-s3e01-002',
    'ex-9c18d9e68e8f',
    'ex-a66a52213812',
    'got-s4e06-001',
    'got-s4e04-020',
    'ex-2c66526efe58',
    'ex-969275f68cbb',
    'ex-1cb9240d9061',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Reference_Grammar',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Adjectives',
    'https://wiki.languageinvention.com/index.php?title=Hae',
    'https://wiki.languageinvention.com/index.php?title=Toliot',
    'https://wiki.languageinvention.com/index.php?title=Hēdrȳ',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Derivational_Affixes',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_numerals',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=Zaldrīzes',
    'https://wiki.languageinvention.com/index.php?title=Zaldrīzdōron',
    'https://wiki.languageinvention.com/index.php?title=Valzȳrys',
    'https://wiki.languageinvention.com/index.php?title=Hunanne',
    'https://wiki.languageinvention.com/index.php?title=Rizmanne',
    'https://wiki.languageinvention.com/index.php?title=Biāllaodio',
    'https://wiki.languageinvention.com/index.php?title=Hūrlilio',
    'https://wiki.languageinvention.com/index.php?title=Onqēlos',
    'https://wiki.languageinvention.com/index.php?title=Kōrȳdragon',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['comparison', 'word-building', 'paateopas'],
  grammarAlso: ['numerals'],
  drills: { degree: ['equative', 'comparative', 'superlative'] },
  showLines: ['hotd-s1e01-020', 'hotd-s2e04-006', 'hotd-s3e01-009', 'got-s7e04-001', 'got-s6e01-002', 'hotd-s2e03-001', 'hotd-s3e01-002', 'got-s4e06-001', 'got-s4e04-020'],
  pronunciation: { focus: 'stress', words: ['pāsābare', 'oznehure', 'kȳvanon', 'gūrēntir', 'brāedāzma'], lines: ['got-s7e04-001', 'got-s6e01-002', 'got-s4e06-001'] },
  letter: {
    fi: 'Vertaa kahta Targaryenia tai lohikäärmettä: kumpi on suurempi, vanhempi tai viisaampi.',
    en: 'Compare two Targaryens or dragons: which is bigger, older or wiser.',
  },
  scene: 'hotd-s3e01-1',
}

# Vertailu ja sananmuodostus

Ensin vertailu: suurempi, suurin ja yhtä suuri. Sitten sananmuodostus, jossa samat ajatukset näkyvät sanojen tasolla: isompi ja pienempi, tekijä ja paikka, yhdyssanat ja luvut.

## Vertailu: suurempi, suurin, yhtä suuri

Suomen adjektiivilla on kolme vertailuastetta: *iloinen, iloisempi, iloisin*. Valyriassa niitä on neljä. Neljäs on **ekvatiivi**, *yhtä iloinen*. Suomessa sille ei ole omaa muotoa, vaan sanotaan *yhtä iloinen kuin*. Duolingo ei käsitellyt vertailua lainkaan, joten tämä oppitunti on kokonaan uutta. Oppitunnin lopussa osaat sanoa *suurempi kuin*, *suurin kaikista*, *yhtä nopea kuin* ja *enemmän kuin*. Lisäksi ymmärrät yhden House of the Dragonin kauneimmista siunauksista.

## Neljä astetta

Wikin kielioppisivun mukaan:

- **komparatiivi** ’X-mpi’ on <Term id="adjektiiviluokka">I-luokan</Term> adjektiivi, ja sen pääte on tyyppiä *-kta*
- **superlatiivi** ’X-in’ on <Term id="adjektiiviluokka">II-luokan</Term> adjektiivi, ja sen pääte on tyyppiä *-je*
- **ekvatiivi** ’yhtä X’ on <Term id="adjektiiviluokka">I-luokan</Term> adjektiivi, ja sen pääte on tyyppiä *-pa*

<Paradigm lemma="kirine" filter="pre.lunar.nom.sg" />

<Compare fi="iloise|mpi" hv="kirin|kta" label="komparatiivi" />

<Compare fi="ilois|in" hv="kirin|je" label="superlatiivi" />

Vartalo voi muuttua: <Hv>kirine</Hv> → <Hv glossed>kirimpa</Hv> ’yhtä iloinen’, <Hv glossed>rōva</Hv> ’suuri’ → <Hv glossed>rōvykta</Hv> ’suurempi’ → <Hv glossed>rōvāje</Hv> ’suurin’. Muotoja ei siis kannata arvata. Katso ne taulukosta tai sanakirjasta. Koska vertailumuodot ovat itse adjektiiveja, ne taipuvat suvun, sijan ja luvun mukaan kuten muutkin adjektiivit.

<Paradigm lemma="rōva" filter="pre.lunar.nom.sg" />

## Suurempi kuin: vertailukohta instrumentaaliin

Kun sanotaan *X on suurempi kuin Y*, Y on **instrumentaalissa**. Sanakirjan esimerkki:

<Example id="ex-23f825fba84a" />

<Interlinear hv="Davido zaldrīzes aōhos zaldrīzose rōvyktys issa." gloss="David-GEN lohikäärme-NOM sinun-INS lohikäärme-INS suuri-CMPR.SOL.NOM olla-PRS.3SG" fi="Davidin lohikäärme on sinun lohikäärmettäsi suurempi." />

Suomessa rakenne on tässä yllättävän samanlainen: *sinun lohikäärmettäsi suurempi*. Vertailukohta tulee ennen komparatiivia omassa sijassaan, suomessa partitiivissa ja valyriassa instrumentaalissa. Jos ajattelet *kuin*-rakenteen sijaan partitiivirakennetta, valyrian järjestys tuntuu luonnolliselta.

Komparatiivista tehdään adverbi samalla tavalla kuin perusmuodosta. Pelko leikkaa *syvemmin kuin miekka*:

<Example id="ex-3c3d21288ab8" />

<Interlinear hv="Zūger korzose trūmȳtrī nektossis." gloss="pelko-NOM.COL miekka-INS syvä-CMPR.ADV leikata-AOR.3SG" fi="Pelko leikkaa syvemmin kuin miekka." />

Baela huutaa lohikäärmeelleen komparatiivista muodostettua adverbia, joka on sanakirjassa omana hakusananaan:

<Line id="hotd-s3e01-009" />

Komparatiivi toimii myös ilman vertailukohtaa, kuten suomessa *viisaampi suunnitelma*:

<Example id="ex-991b56eca6b7" />

<Example id="ex-7f34a1239c38" />

## Yhtä … kuin: ekvatiivi

Ekvatiivissa vertailukohta on myös instrumentaalissa. Wikin esimerkkilauseessa on kolme ekvatiivia peräkkäin:

<Example id="ex-453c664c3c21" />

<Interlinear hv="Sȳndroso lykāpa, myrdomy aderpa, iēdrosa gīdāpa." gloss="varjo-INS hiljainen-EQU peura-INS nopea-EQU vesi-INS tyyni-EQU" fi="Hiljainen kuin varjo, nopea kuin peura, tyyni kuin seisova vesi." />

Laenan hautajaisissa Vaemond Velaryon siunaa veljentytärtään. Siunauksessa on ekvatiivi ja vertailukohta instrumentaalissa, ja koko lause on kolmannen persoonan toivotus. Wikin mukaan toivotuksen subjekti on **vokatiivissa** ja verbi infinitiivissä (*olkoot tuulesi…*, <LessonLink n={42}>oppitunti 42</LessonLink>).

<Example id="ex-c95a57401155" />

<Interlinear hv="Inkoso kostōbāpis aōhis jelmīs sagon," gloss="selkä-INS vahva-EQU.VOC.PL sinun-VOC.PL tuuli-VOC.PL olla-INF" fi="Olkoot tuulesi yhtä vahvat kuin selkäsi," />

<Interlinear hv="gīso lykāpas aōhas embis," gloss="henki-INS tyyni-EQU.AQ.VOC.PL sinun-AQ.VOC.PL meri-VOC.PL" fi="meresi yhtä tyynet kuin mielesi," />

<Interlinear hv="se prūmȳsa lēdāpas aōhas manengīs." gloss="ja sydän-INS täysi-EQU.VOC.PL sinun-VOC.PL verkko-VOC.PL" fi="ja verkkosi yhtä täydet kuin sydämesi." />

Rinnakkaisia lauseita on kolme, ja viimeinen alkaa sanalla <Hv l="se">se</Hv>, kuten kolmen tai useamman jäsenen luettelossa on tavallista (<LessonLink n={47}>oppitunti 47</LessonLink>).

Wiki mainitsee kaksi muuta ekvatiivirakennetta:

- **<Hv>hae</Hv> + lokatiivi**: <Hv glossed>Hae skorot vala bōsāpa issa?</Hv>, sanatarkasti ’kuin mikä mies on yhtä pitkä?’ eli ’kuinka pitkä mies on?’.
- **<Hv>hen</Hv> + datiivi**, kun tarkoitetaan *yhtä X kuin kuka tahansa*:

<Example id="ex-051788fbace1" />

<Example id="ex-c63fa45474e2" />

Wiki sanoo suoraan, että näiden rakenteiden tarkka työnjako on vielä epäselvä. Turvallisin valinta omiin lauseisiin on instrumentaali.

## Suurin: superlatiivi ja hēdrȳ

Superlatiivi on ’X-in’. Kun halutaan sanoa *X:istä paras*, voidaan käyttää postpositiota <Hv glossed>hēdrȳ</Hv> ’joukosta’ genetiivin kanssa. Daenerys kehuu Harmaata Matoa:

<Example id="ex-34b291c49825" />

<Interlinear hv="Nēdyro mentyrō hēdrȳ pāsābarje karājē iksā." gloss="urhea-GEN.COL sotilas-GEN.COL=ja joukosta uskollinen-SUPL suuri-SUPL=ja olla-PRS.2SG" fi="Olet urheista ja sotilaista uskollisin ja suurin." />

Lauseessa on kaksi <LessonLink n={47}>oppitunnilla 47</LessonLink> käsiteltyä pidennystä: <Hv glossed>mentyrō</Hv> (*ja sotilaista*) ja <Hv glossed>karājē</Hv> (*ja suurin*). Englanninkielinen käännös on vapaampi (*the bravest of men, the most loyal of soldiers*).

Kirjoitetussa tekstissä superlatiiveja voi olla monta peräkkäin. Tyrian kirjastoa kuvataan näin:

<Example id="ex-c8933dfdc449" />

Superlatiivilla voi myös vahvistaa, kuten suomessa *mitä suurimmalla ilolla*. Daemon vastaa Rhaenyralle:

<Example id="ex-a8cb7727a1a8" />

<Note>
Daemonin <Hv fi="korkein">eglijior</Hv> ei ole aivan sama muoto kuin adjektiivin <Hv l="eglie">eglie</Hv> superlatiivi sovelluksen taivutustaulukossa (maasuvussa <Hv>eglijor</Hv>). Sen voi liputtaa. Kirjastolauseen <Hv l="rhēdessiarza-2">rhēdessiarzājōr</Hv> taas on tavallinen superlatiivi <Hv l="rhēdessiarza-2">rhēdessiarzājor</Hv> konjunktiivisella pidennyksellä (<LessonLink n={47}>oppitunti 47</LessonLink>): <Hv glossed>rōvājor rhēdessiarzājōr</Hv> on ’suurin ja kuuluisin’ (wikin sanakirja, *Rhēdessiarza*).
</Note>

## Enemmän kuin: toliot

Kun vertaillaan tekemistä eikä ominaisuutta (*rakastaa enemmän kuin*), käytetään postpositiota <Hv glossed>toliot</Hv> ’yllä, yli’, jolle sanakirja antaa myös merkityksen ’enemmän kuin’. Sen edellä on genetiivi tai infinitiivi.

<Line id="hotd-s1e01-020" />

<Interlinear hv="Kepa aōha avy sīr ojūdo tubiro toliot jorrāelza." gloss="isä-NOM sinun-NOM sinä-ACC nyt menetetty-GEN päivä-GEN.COL yli tarvita-PRS.3SG" fi="Isäsi tarvitsee sinua nyt enemmän kuin koskaan." />

Ilmaus <Hv glossed>ojūdo tubiro toliot</Hv> ’enemmän kuin koskaan’ on sanakirjassa omana hakusananaan. Kirjaimellisesti se tarkoittaa jotakin sellaista kuin *menneiden päivien yli*. Nuori Rhaenyra käyttää samaa postpositiota persoonapronominin kanssa: <Hv glossed>aō toliot</Hv> ’enemmän kuin sinua’.

<Line id="hotd-s2e04-006" />

## Kokoa itse

<Practice hv="Zaldrīzes annose rōvyktys issa." fi="Lohikäärme on hevosta suurempi." />

<Practice hv="Riña myrdomy aderpa issa." fi="Tyttö on yhtä nopea kuin peura." />

<Practice hv="Kesys zaldrīzes rōvāje issa." fi="Tämä lohikäärme on suurin." />

Muistisääntö: vertailukohta laitetaan instrumentaaliin ja sen perään komparatiivi tai ekvatiivi. Se on kuin suomen partitiivirakenne *hevosta suurempi*, mutta sijana on instrumentaali.

## Sananmuodostus: johtimet, yhdyssanat ja luvut

Suomalainen tunnistaa sananmuodostuksen helposti. Kun tiedät, että *kirjoittaja*, *kahvila* ja *kasvoton* koostuvat palasista, pystyt arvaamaan satoja sanoja, joita et ole koskaan nähnyt. Valyria rakentaa sanastonsa samalla tavalla. Tällä oppitunnilla käydään läpi tärkeimmät substantiivi- ja adjektiivijohtimet, yhdyssanat sekä se, miten satojen luvut ja järjestysluvut on koottu. Verbien johtimista, kuten applikatiiveista ja muista etuliitteistä, puhuttiin verbiosuudessa (<LessonLink n={45}>oppitunti 45</LessonLink>).

Pääasiallisena lähteenä on wikin johdinsivu (*Derivational Affixes*). Sen on koonnut eräs wikin käyttäjä Petersonin aineistosta, ja epävarmat tapaukset on merkitty siinä sanalla ”ehkä”. Tässä esitetään vain johtimet, joista on selviä esimerkkejä.

## Isompi ja pienempi

**Augmentatiivi** ’iso, suuri X’. Wikin kielioppisivun mukaan <Hv>-āzma</Hv> on hyvin tuottava johdin, eli sillä syntyy helposti uusia sanoja:

- <Hv glossed>brāedion</Hv> ’kupari’ → <Hv glossed>brāedāzma</Hv> ’pronssi’ (”iso kupari”)
- <Hv glossed>jelmio</Hv> ’tuuli’ → <Hv glossed>jelmāzma</Hv> ’myrsky’ (*Daenerys Jelmāzmo*, Myrskysyntyinen)
- <Hv glossed>nūmo</Hv> ’siemen’ → <Hv glossed>nūmāzma</Hv> ’merkitys, ydin’
- <Hv glossed>vīlībāzma</Hv> ’sota’ (vrt. <Hv glossed>vīlībagon</Hv> ’taistella’)

Muita augmentatiivijohtimia ovat <Hv>-albar</Hv> (<Hv glossed>geron</Hv> ’polku’ → <Hv glossed>geralbar</Hv> ’valtatie’, <Hv glossed>egros</Hv> ’terä’ → <Hv glossed>egralbar</Hv> ’keihäs’) ja <Hv>-io</Hv> (<Hv glossed>kēli</Hv> ’kissa’ → <Hv glossed>kēlio</Hv> ’leijona’).

**Deminutiivi** ’pieni X’. Yleisin johdin on <Hv>-ītsos</Hv>: <Hv l="riña">riña</Hv> → <Hv glossed>riñītsos</Hv> ’pikkutyttö’, <Hv>zokla</Hv> → <Hv glossed>zoklītsos</Hv> ’sudenpentu’, <Hv glossed>perzys</Hv> ’tuli’ → <Hv glossed>perzītsos</Hv> ’pieni liekki’. Viimeisellä sanalla Daenerys puhuttelee haavoittunutta Drogonia:

<Line id="got-s7e04-001" />

Suomessa saman sävyn antavat esimerkiksi *-nen*- ja *-kainen*-johtimet (*tyttönen*, *lapsukainen*). Kaksi muuta deminutiivijohdinta muuttaa merkitystä enemmän:

- <Hv>-iapos</Hv>: <Hv glossed>pirtir</Hv> ’valhe’ (< <Hv glossed>pirta</Hv> ’väärä’) → <Hv glossed>pirtiapos</Hv> ’vitsi’, <Hv l="rūni" glossed>rūni</Hv> ’muisto’ → <Hv glossed>rūniapos</Hv> ’kirje, viesti’, <Hv glossed>brōzi</Hv> ’nimi’ → <Hv glossed>brōziapos</Hv> ’lempinimi’
- <Hv>-anna</Hv>: <Hv glossed>bartos</Hv> ’pää’ → <Hv glossed>bartanna</Hv> ’kallo’

<Note>
Pienennysjohdin <Hv>-ītsos</Hv> ei aina tarkoita pientä samaa asiaa. Joskus se nimeää osan isommasta: <Hv glossed>tōmo</Hv> ’selja’ → <Hv glossed>tōmītsos</Hv> ’seljanmarja’. Havainto on Damien Halmen (Academy of High Valyrian).
</Note>

## Tekijä, paikka, teko, ominaisuus

Näiden johdinten suomalaiset vastineet ovat ilahduttavan tarkkoja.

**Tekijä**: <Hv>-tys</Hv> (suomessa *-ja*). Sitä käytetään erityisesti ammattinimityksissä: <Hv glossed>voktys</Hv> ’pappi’, <Hv glossed>loktys</Hv> ’merimies’, <Hv glossed>mentys</Hv> ’sotilas’, <Hv glossed>zentys</Hv> ’vieras’. Verbeistä johdetaan tekijöitä myös johtimella <Hv>-io</Hv> tai <Hv>-kio</Hv>: <Hv glossed>kaerīnio</Hv> ’pelastaja’, <Hv glossed>sētekio</Hv> ’seppä’, <Hv glossed>qrimpālekio</Hv> ’petturi’.

**Paikka**: <Hv>-lion</Hv> tai <Hv>-ion</Hv> verbeistä (suomessa *-mo*, *-la*): <Hv glossed>dēmalion</Hv> ’valtaistuin’ (”istumapaikka”), <Hv glossed>sindilion</Hv> ’tori’ (”ostopaikka”). Substantiiveista paikka johdetaan johtimella <Hv>-urlion</Hv>: <Hv glossed>zentys</Hv> ’vieras’ → <Hv glossed>zenturlion</Hv> ’majatalo’, <Hv glossed>sōnar</Hv> ’talvi’ → <Hv glossed>Sōdrurlion</Hv> ’Winterfell’, <Hv glossed>laes</Hv> ’silmä’ → <Hv glossed>laehurlion</Hv> ’kasvot’ (”silmien paikka”).

**Teko**: <Hv>-non</Hv> (suomessa *-nti*, *-us*): <Hv glossed>ērinnon</Hv> ’voitto’, <Hv glossed>iderennon</Hv> ’valinta’, <Hv glossed>raqnon</Hv> ’rakkaus’, <Hv glossed>vaoreznon</Hv> ’mieltymys’. Tapahtumaa kuvaa <Hv>-nes</Hv>: <Hv glossed>iderennes</Hv> ’oikeudenkäynti’.

**Ominaisuus**: <Hv>-ves</Hv>, <Hv>-āves</Hv> tai <Hv>-ives</Hv> adjektiivista (suomessa *-us*, *-uus*): <Hv glossed>dāez</Hv> ’vapaa’ → <Hv glossed>dāerves</Hv> ’vapaus’, <Hv glossed>gevie</Hv> ’kaunis’ → <Hv glossed>gevives</Hv> ’kauneus’, <Hv>kirine</Hv> → <Hv glossed>kirimves</Hv> ’onni’.

**Väline**: <Hv>-vos</Hv> verbistä: <Hv glossed>hepvos</Hv> ’tikkaat’ (*kiivetä*), <Hv glossed>mīsvos</Hv> ’haarniska’ (*suojella*), <Hv glossed>ohīlvos</Hv> ’tikari’ (*pistää*).

**Sivutuote**: <Hv>-illa</Hv>: <Hv glossed>avero</Hv> ’rypäle’ → <Hv glossed>averilla</Hv> ’viini’, <Hv glossed>pungilla</Hv> ’sierain’. Tyrion sanoi valyriansa olevan vähän ”sierain” (<Hv>pungilla</Hv>), vaikka tarkoitti ”kulunut” (<Hv>puñila</Hv>), ja Missandei korjasi hänet.

<Note>
Johdin voi muuttaa vartalon loppua, kuten Damien Halme (Academy of High Valyrian) huomauttaa. Teon <Hv>-non</Hv> tekee *s*:stä *z*:n (<Hv>vaoresagon</Hv> → <Hv glossed>vaoreznon</Hv> ’mieltymys’) ja *b*:stä *n*:n (<Hv>iderēbagon</Hv> → <Hv glossed>iderennon</Hv> ’valinta’). Tekijän johdin voi olla myös <Hv>-rys</Hv>: <Hv glossed>arghugon</Hv> ’metsästää’ → <Hv glossed>arghurys</Hv> ’metsästäjä’.
</Note>

## Adjektiivit

- <Hv>-enka</Hv> ’X:n kaltainen’ (suomessa *-mainen*): <Hv glossed>nēdenka</Hv> ’rohkea’, <Hv glossed>quptys</Hv> ’pakana’ → <Hv glossed>quptenka</Hv> ’yleinen, rahvaan’. Thoros kysyy, puhuuko Melisandre yleiskieltä, ja käyttää ilmausta <Hv glossed>Quptenkos Ēngoso</Hv> ’rahvaan kielellä’ (kieli on instrumentaalissa).
- <Hv>-īha</Hv> ’jostakin paikasta’ (suomessa *-lainen*): <Hv glossed>Valyrīha</Hv> ’valyrialainen’, <Hv glossed>Volantīha</Hv> ’volantislainen’, <Hv glossed>endīha</Hv> ’läntinen’.
- <Hv>-ōñe</Hv> ’jostakin peräisin’: <Hv glossed>embōñe</Hv> ’vesi-’ (vesisuku!), <Hv glossed>vējōñe</Hv> ’eteläinen’.
- <Hv>-qitta</Hv> ’ilman X:ää’ (suomessa *-ton/-tön*): <Hv glossed>laehurloqitta</Hv> ’kasvoton’, <Hv glossed>mōrqitta</Hv> ’loputon’.
- <Hv>-ēgrie</Hv> vahvistaa: <Hv>rōva</Hv> → <Hv glossed>rōvēgrie</Hv> ’valtava’, <Hv glossed>quba</Hv> ’huono’ → <Hv glossed>qupēgrie</Hv> ’kamala’. Varys arvioi Tyrionin valyriaa:

<Line id="got-s6e01-002" />

Kaksi kieltävää etuliitettä toimii eri tavoin. **<Hv>do-</Hv>, <Hv>dor-</Hv>** on pelkkä kielto (suomessa *-ton/-tön* tai *epä-*): <Hv glossed>Dovaogēdy</Hv> ’Tahraton’, <Hv glossed>Dorzālty</Hv> ’Palamaton’. **<Hv>nā-</Hv>** tarkoittaa ’X:n vastakohta’: <Hv glossed>kostōba</Hv> ’vahva’ → <Hv glossed>nākostōba</Hv> ’heikko’, <Hv glossed>qopsa</Hv> ’vaikea’ → <Hv glossed>nāqopsa</Hv> ’helppo’, <Hv glossed>nāmorghūlilare</Hv> ’kuolematon’. Wiki korostaa eroa: *heikko* ei ole ’ei-vahva’ vaan vahvan vastakohta.

## Yhdyssanat

Valyrian yhdyssanoissa määriteosa tulee ensin ja perusosa viimeisenä, aivan kuten suomessa (*lohikäärme|kivi*). Ensimmäinen osa lyhenee usein. Esimerkkejä wikin sanakirja-artikkelien etymologioista:

- <Hv glossed>zaldrīzes</Hv> ’lohikäärme’ < <Hv glossed>zālare</Hv> ’palava’ + <Hv glossed>rīza</Hv> ’lisko’. Lohikäärme on siis sanatarkasti *palava lisko*.
- <Hv glossed>Zaldrīzdōron</Hv> ’Lohikäärmekivi’ < <Hv>zaldrīzes</Hv> + <Hv glossed>dōron</Hv> ’kivi’
- <Hv glossed>valzȳrys</Hv> ’aviomies’ ja <Hv glossed>ābrazȳrys</Hv> ’vaimo’ < <Hv>vala</Hv>, <Hv>ābra</Hv> + <Hv>zȳrys</Hv>
- <Hv glossed>hunanne</Hv> ’aasi’ < <Hv glossed>hunes</Hv> ’jänis’ + <Hv glossed>anne</Hv> ’hevonen’, <Hv glossed>rizmanne</Hv> ’kameli’ < <Hv glossed>rizmon</Hv> ’hiekka’ + <Hv>anne</Hv>
- <Hv glossed>Onqēlos</Hv> ’Aamutähti’ (naisen nimi) < <Hv glossed>ōz</Hv> ’aamunkoitto’ + <Hv glossed>qēlos</Hv> ’tähti’
- <Hv glossed>kōrȳdragon</Hv> ’pilkata pyhää’ < <Hv glossed>kōz</Hv> ’paha’ + <Hv glossed>ȳdragon</Hv> ’puhua’, <Hv glossed>pirtȳdragon</Hv> ’antaa väärää tietoa’ < <Hv glossed>pirta</Hv> ’väärä’ + <Hv>ȳdragon</Hv>

House of the Dragonin lohikäärmeiden nimissä yhdistyvät yhdyssana ja tekijäjohdin. <Hv glossed>Hūrlilio</Hv> ’Moondancer’ (sanatarkasti *kuuntanssija*) tulee sanoista <Hv glossed>hūra</Hv> ’kuu’ + <Hv glossed>lilagon</Hv> ’tanssia’ + <Hv>-io</Hv>, ja <Hv glossed>Biāllaodio</Hv> ’Sheepstealer’ (sanatarkasti *lampaanvaras*) sanoista <Hv glossed>bianor</Hv> ’lammas’ + <Hv glossed>laodigon</Hv> ’varastaa’ + <Hv>-io</Hv>.

<Line id="hotd-s2e03-001" />

<Line id="hotd-s3e01-002" />

Myös monet pikkusanat ovat yhdyssanoja: <Hv l="sepār-2">sepār</Hv> < <Hv l="se">se</Hv> + <Hv glossed>pār</Hv> ’silloin, sitten’, <Hv>hēzīr</Hv> < <Hv>hen</Hv> + <Hv glossed>sīr</Hv> ’nyt’, <Hv>kessyt</Hv> < <Hv l="kesa">kesa</Hv> + <Hv glossed>syt</Hv> ’jotakin varten’.

## Luvut sanoina

<LessonLink n={25}>Oppitunnilla 25</LessonLink> opit luvut 1–999 ja tuhannen. Kahdessa kohdassa näkyy kuitenkin sananmuodostusta, joka kannattaa katsoa tarkemmin.

**Sadat ovat yhdyssanoja.** <Hv glossed>gār</Hv> ’100’ liitetään lukusanan (2–9) perään, ja ensimmäinen osa lyhenee, joskus epäsäännöllisesti: <Hv>langār</Hv> (2 × 100), <Hv>hārgār</Hv>, <Hv>zūgār</Hv> (< <Hv>izula</Hv>!), <Hv>tōngār</Hv>, <Hv>bȳrgār</Hv>, <Hv>sīgār</Hv>, <Hv>jēngār</Hv>, <Hv>vōrgār</Hv>. Kymmenillä on omat sanansa, joilla on yhteinen loppu <Hv>-ēpsa</Hv>: <Hv glossed>lantēpsa</Hv> ’20’, <Hv glossed>hārēpsa</Hv> ’30’.

Duolingon sääntö (<LessonLink n={25}>oppitunti 25</LessonLink>) on, että satojen ja loppuosan väliin tulee <Hv l="se">se</Hv>:

<Example id="ex-2c66526efe58" />

<Example id="ex-969275f68cbb" />

Kuten <LessonLink n={25}>oppitunnilla 25</LessonLink> huomattiin, Daenerys kuitenkin jättää sanan <Hv l="se">se</Hv> pois, kun hän kertoo Meereenin ristiinnaulittujen lasten määrän:

<Line id="got-s4e04-020" />

Tätä eroa wiki ei selitä. Noudata omissa lauseissasi Duolingon sääntöä, mutta opi tunnistamaan myös lyhyempi muoto. Tuhannet ovat substantiiveja, ja laskettava on monikon genetiivissä:

<Example id="ex-1cb9240d9061" />

Wikin numeroliitteen mukaan Peterson on aiemmin ehdottanut myös muita tapoja ilmaista tuhansien väliin jäävät luvut. Liite toteaa itse, että ne ovat ristiriidassa pääsäännön kanssa ja saattavat olla väliaikaisia ideoita.

**Järjestysluvut** ovat <Term id="adjektiiviluokka">III-luokan</Term> adjektiiveja, ja ne muodostetaan johtimella <Hv>-lie</Hv> (myös <Hv>-rie</Hv>, <Hv>-nie</Hv>). Ensimmäiset niistä ovat epäsäännöllisiä, kuten suomessakin (*yksi* → *ensimmäinen*, *kaksi* → *toinen*):

- 1. <Hv l="ēlie">ēlie</Hv>, 2. <Hv l="tȳne">tȳne</Hv>, 3. <Hv>saelie</Hv>, 4. <Hv>izunnie</Hv>, 5. <Hv>tōmelie</Hv>
- 6. <Hv>byllie</Hv>, 7. <Hv>sīglie</Hv>, 8. <Hv>jēnqelie</Hv>, 9. <Hv>vollie</Hv>, 10. <Hv>amplie</Hv>
- 11. <Hv>kūrie</Hv>, 12. <Hv>ñallie</Hv>; tästä eteenpäin rinnastuksella: 13. <Hv glossed>saelie ampā</Hv>
- 20. <Hv>lantīblie</Hv>, 100. <Hv>gallie</Hv>, 1000. <Hv>pyrȳzlie</Hv>

<Note>
Kymmenten järjestysluvut ovat säännöllisiä, kuten Damien Halme (Academy of High Valyrian) huomauttaa: kaikki päättyvät <Hv>-īblie</Hv>. 20. <Hv>lantīblie</Hv>, 30. <Hv>hārīblie</Hv>, 40. <Hv>izulīblie</Hv>, 50. <Hv>tōmīblie</Hv>, 60. <Hv>bȳrīblie</Hv>, 70. <Hv>sīkudīblie</Hv>, 80. <Hv>jēnqīblie</Hv>, 90. <Hv>vōrīblie</Hv>.
</Note>

Järjestysluku taipuu kuten mikä tahansa adjektiivi:

<Example id="ex-9c18d9e68e8f" />

<Example id="ex-a66a52213812" />

Missandein titteliluettelossa on *Nimensä ensimmäinen*, <Hv glossed>Zȳho Brōzio Ēlos</Hv>. Siinä <Hv glossed>ēlos</Hv> on oma substantiivinsa, ’ensimmäinen’:

<Line id="got-s4e06-001" />

<Paradigm lemma="ēlie" filter="positive.pre.lunar" numbers="sg" />

## Kokoa itse

<Practice hv="Riñītsos kirine issa." fi="Pikkutyttö on iloinen." />

<Practice hv="Ñuhys valzȳrys nēdenkys issa." fi="Mieheni on rohkea." />

Kun seuraavan kerran sanakirjassa tulee vastaan tuntematon sana, katso ensin sen loppua (<Hv>-tys</Hv>, <Hv>-lion</Hv>, <Hv>-ves</Hv>, <Hv>-qitta</Hv>…) ja sitten alkua (<Hv>nā-</Hv>, <Hv>do-</Hv>). Usein merkityksen voi päätellä, ennen kuin luet käännöksen.

## Sanat

<Vocab />

<Check />
